import { Link } from "react-router-dom";
import { useMemo } from "react";
import { CalendarDays, RefreshCw, User, Stethoscope, Clock, ExternalLink, ArrowRight, CheckCircle2 } from "lucide-react";
import Seo from "../components/Seo";
import { useI18n } from "../i18n/LanguageProvider";
import { useRegisterAlternates } from "../components/layout/Layout";
import { articlePath, entityPath, pagePath } from "../config/pages";
import { getArticle } from "../data/articles";
import { getTreatment } from "../data/treatments";
import { CTABand } from "../components/Sections";
import Breadcrumbs from "../components/Breadcrumbs";
import { Reveal } from "../components/motion/Reveal";
import { ArticleCard } from "../components/Cards";
import { articleSchema, breadcrumbSchema } from "../lib/schema";

function fmtDate(iso, lang) {
  try {
    return new Date(iso).toLocaleDateString(lang === "fr" ? "fr-FR" : lang === "de" ? "de-DE" : "en-GB", {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function ArticlePage({ articleKey }) {
  const { t, lang, p, e } = useI18n();
  const ar = getArticle(articleKey);
  const alternates = {
    en: articlePath(ar.slug, "en"),
    fr: articlePath(ar.slug, "fr"),
    de: articlePath(ar.slug, "de"),
  };
  useRegisterAlternates(alternates);

  const title = ar.title[lang] || ar.title.en;
  const path = articlePath(ar.slug, lang);
  const sections = ar.sections.map((s) => ({ id: s.id, h: s.h[lang] || s.h.en, body: s.body[lang] || s.body.en }));
  const related = (ar.relatedTreatments || []).map(getTreatment).filter(Boolean);
  const relatedArticles = (ar.relatedArticles || []).map(getArticle).filter(Boolean);

  const crumbs = [
    { name: t.nav.blog, to: pagePath("blog", lang) },
    { name: title, to: path },
  ];

  const jsonLd = useMemo(() => [
    articleSchema({
      headline: title,
      description: ar.excerpt[lang] || ar.excerpt.en,
      path,
      image: ar.image,
      author: ar.author,
      reviewer: ar.reviewer,
      published: ar.published,
      updated: ar.updated,
    }),
    breadcrumbSchema([{ name: t.common.home, path: pagePath("home", lang) }, ...crumbs.map((c) => ({ name: c.name, path: c.to }))]),
  ], [lang]);

  return (
    <>
      <Seo
        title={ar.metaTitle[lang] || ar.metaTitle.en}
        description={ar.metaDesc[lang] || ar.metaDesc.en}
        path={path}
        alternates={alternates}
        lang={lang}
        image={ar.image}
        type="article"
        jsonLd={jsonLd}
      />

      {/* Header */}
      <section className="bg-mist">
        <div className="container-dv py-12 lg:py-16 max-w-4xl">
          <Breadcrumbs crumbs={crumbs} />
          <p className="mt-6 label-eyebrow">{ar.category[lang] || ar.category.en}</p>
          <h1 className="mt-3 font-serif text-3xl leading-[1.1] tracking-tight text-navy sm:text-4xl lg:text-5xl">{title}</h1>
          <p className="mt-5 text-lg leading-relaxed text-navy/70">{ar.excerpt[lang] || ar.excerpt.en}</p>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-navy/60">
            <span className="inline-flex items-center gap-2"><User className="h-4 w-4 text-dental" /> {t.common.author}: {ar.author}</span>
            <span className="inline-flex items-center gap-2"><Stethoscope className="h-4 w-4 text-dental" /> {t.common.medicalReviewer}: {ar.reviewer}</span>
            <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-dental" /> {ar.readTime} {t.common.readTime}</span>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-navy/50">
            <span className="inline-flex items-center gap-2"><CalendarDays className="h-4 w-4" /> {t.common.published}: {fmtDate(ar.published, lang)}</span>
            <span className="inline-flex items-center gap-2"><RefreshCw className="h-4 w-4" /> {t.common.updated}: {fmtDate(ar.updated, lang)}</span>
          </div>
        </div>
      </section>

      <div className="container-dv py-12 lg:py-16">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          {/* TOC */}
          <aside className="lg:col-span-3 order-2 lg:order-1">
            <div className="lg:sticky lg:top-28">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-navy/45">{t.common.tableOfContents}</p>
              <nav className="mt-4 space-y-2" data-testid="article-toc">
                {sections.map((s) => (
                  <a key={s.id} href={`#${s.id}`} className="block text-sm text-navy/65 hover:text-dental transition-colors leading-snug">
                    {s.h}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Body */}
          <article className="lg:col-span-9 order-1 lg:order-2 max-w-3xl">
            <div className="overflow-hidden rounded-[1.75rem] shadow-soft">
              <img src={ar.image} alt={title} className="h-64 w-full object-cover sm:h-80" />
            </div>

            {ar.keyTakeaways && (
              <div className="mt-8 rounded-2xl border border-turquoise/20 bg-turquoise/5 p-6" data-testid="article-key-takeaways">
                <h2 className="flex items-center gap-2 text-xl text-navy">
                  <CheckCircle2 className="h-5 w-5 text-turquoise" />
                  {t.common.keyTakeaways}
                </h2>
                <ul className="mt-4 space-y-2.5">
                  {(ar.keyTakeaways[lang] || ar.keyTakeaways.en).map((k, i) => (
                    <li key={i} className="flex gap-3 text-[15px] leading-relaxed text-navy/75">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-turquoise" />
                      <span>{k}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="prose-dv mt-10">
              {sections.map((s) => (
                <section key={s.id} id={s.id}>
                  <h2>{s.h}</h2>
                  {s.body.map((para, i) => <p key={i}>{para}</p>)}
                </section>
              ))}
            </div>

            {/* References */}
            {ar.references?.length > 0 && (
              <div className="mt-12 rounded-2xl border border-navy/10 bg-white p-6 shadow-soft">
                <h2 className="text-xl text-navy">{t.common.references}</h2>
                <ul className="mt-4 space-y-2">
                  {ar.references.map((ref, i) => (
                    <li key={i}>
                      <a href={ref.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-dental hover:text-dental-dark">
                        {ref.text}
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Disclaimer */}
            <Reveal>
              <div className="mt-8 rounded-2xl border border-navy/10 bg-mist p-6 text-sm leading-relaxed text-navy/70">
                <strong className="text-navy">{t.common.disclaimerTitle}.</strong> {t.common.disclaimer}
              </div>
            </Reveal>

            {/* CTA */}
            <div className="mt-8 flex flex-col gap-3 rounded-2xl bg-dental/5 border border-dental/15 p-6 sm:flex-row sm:items-center sm:justify-between">
              <p className="font-semibold text-navy">{t.ctaBand.title}</p>
              <Link to={p("contact")} data-testid="article-cta" className="inline-flex items-center justify-center gap-2 rounded-full bg-dental px-6 py-3 text-sm font-semibold text-white hover:bg-dental-dark transition-colors">
                {t.cta.myFreePlan}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            {/* Related treatments */}
            {related.length > 0 && (
              <div className="mt-12">
                <h2 className="text-xl text-navy">{t.common.relatedTreatments}</h2>
                <div className="mt-4 flex flex-wrap gap-2.5">
                  {related.map((rt) => (
                    <Link key={rt.key} to={e(rt.slug)} className="rounded-full border border-navy/12 bg-white px-4 py-2 text-sm font-semibold text-navy hover:border-dental hover:text-dental transition-colors">
                      {rt.name[lang] || rt.name.en}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </article>
        </div>
      </div>

      {/* Related articles */}
      {relatedArticles.length > 0 && (
        <section className="bg-mist py-16 lg:py-20">
          <div className="container-dv">
            <h2 className="text-2xl sm:text-3xl text-navy">{t.common.relatedArticles}</h2>
            <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {relatedArticles.map((ra) => (
                <ArticleCard key={ra.key} article={ra} />
              ))}
            </div>
          </div>
        </section>
      )}

      <CTABand />
    </>
  );
}
